import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import apiClient from '@/services/api'
import {
    resourceService, RESOURCE_KIND_LABEL, REQUEST_STATUS_LABEL,
    type Deployment, type ResourceKind, type ResourceRequest, type ResourceSummary, type RequestStatus,
} from '@/services/emergencyService'

const inputCls = 'w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-blue-500/50 focus:outline-none'
const btnPrimary = 'rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50'
const btnGhost = 'rounded-xl border border-white/10 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5'

const STATUS_COLOR: Record<RequestStatus, string> = {
    PENDING: 'bg-amber-500/15 text-amber-300', ASSIGNED: 'bg-emerald-500/15 text-emerald-300', PARTIAL: 'bg-sky-500/15 text-sky-300',
    REJECTED: 'bg-rose-500/15 text-rose-300', CANCELLED: 'bg-gray-500/15 text-gray-400',
}
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' }) : '—')
const errorMessage = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
    ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback

interface TenantUser { id: string; fullName: string; username: string; active?: boolean }

/** Recursos de la emergencia: resumen, solicitudes con respuesta y despliegues. */
export default function ResourcesTab({ emergencyId, open, canCommand }: { emergencyId: number; open: boolean; canCommand: boolean }) {
    const [summary, setSummary] = useState<ResourceSummary | null>(null)
    const [requests, setRequests] = useState<ResourceRequest[]>([])
    const [deployments, setDeployments] = useState<Deployment[]>([])
    const [users, setUsers] = useState<TenantUser[]>([])
    const [reqForm, setReqForm] = useState({ kind: 'PERSONNEL' as ResourceKind, quantity: '1', description: '', target: '', neededBy: '' })
    const [depForm, setDepForm] = useState({ kind: 'PERSONNEL' as ResourceKind, userId: '', label: '', organization: '', mission: '', sector: '', plannedUntil: '' })
    const [responding, setResponding] = useState<{ id: number; status: RequestStatus; assignedQty: string; note: string } | null>(null)

    const load = useCallback(async () => {
        try {
            const [s, r, d] = await Promise.all([resourceService.summary(emergencyId), resourceService.requests(emergencyId), resourceService.deployments(emergencyId)])
            setSummary(s); setRequests(r); setDeployments(d)
        } catch { toast.error('No se pudieron cargar los recursos') }
    }, [emergencyId])
    useEffect(() => { load() }, [load])
    useEffect(() => {
        if (!canCommand) return
        apiClient.get<TenantUser[]>('/api/v1/users').then((r) => setUsers((r.data ?? []).filter((u) => u.active !== false))).catch(() => setUsers([]))
    }, [canCommand])

    const act = async (fn: () => Promise<unknown>, ok: string) => {
        try { await fn(); toast.success(ok); load() } catch (e) { toast.error(errorMessage(e, 'No se pudo aplicar el cambio')) }
    }

    const submitRequest = (ev: React.FormEvent) => {
        ev.preventDefault()
        act(() => resourceService.request(emergencyId, {
            kind: reqForm.kind, quantity: Number(reqForm.quantity), description: reqForm.description,
            target: reqForm.target || undefined, neededBy: reqForm.neededBy || undefined,
        }).then(() => setReqForm({ ...reqForm, quantity: '1', description: '', target: '', neededBy: '' })), 'Solicitud registrada')
    }

    const submitDeploy = (ev: React.FormEvent) => {
        ev.preventDefault()
        act(() => resourceService.deploy(emergencyId, {
            kind: depForm.kind, userId: depForm.userId || undefined, label: depForm.label || undefined, organization: depForm.organization || undefined,
            mission: depForm.mission || undefined, sector: depForm.sector || undefined, plannedUntil: depForm.plannedUntil || undefined,
        }).then(() => setDepForm({ ...depForm, userId: '', label: '', mission: '', sector: '', plannedUntil: '' })), 'Recurso desplegado')
    }

    const submitResponse = (ev: React.FormEvent) => {
        ev.preventDefault()
        if (!responding) return
        const r = responding
        act(() => resourceService.respond(emergencyId, r.id, {
            status: r.status, assignedQty: r.status === 'PARTIAL' ? Number(r.assignedQty) : undefined, note: r.note || undefined,
        }).then(() => setResponding(null)), 'Solicitud respondida')
    }

    const extend = (d: Deployment) => {
        const v = window.prompt('Nueva fecha y hora de relevo (AAAA-MM-DDTHH:MM):', d.plannedUntil?.slice(0, 16) ?? '')
        if (!v) return
        act(() => resourceService.extend(emergencyId, d.id, v.length === 16 ? `${v}:00` : v), 'Relevo actualizado')
    }

    const pctColor = !summary ? '' : summary.committedPct > 25 ? 'text-rose-300' : summary.committedPct >= 15 ? 'text-amber-300' : 'text-emerald-300'

    return (
        <div className="space-y-6">
            {summary && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Tile value={summary.activeDeployments} label="recursos desplegados" />
                    <Tile value={summary.personnelDeployed} label="personas en esta emergencia" />
                    <Tile value={`${summary.committedPct}%`} label={`fuerza comprometida (${summary.personnelDeployedAnywhere} de ${summary.personnelAvailable})`} valueClass={pctColor} />
                    <Tile value={summary.pendingRequests} label="solicitudes pendientes" />
                </div>
            )}

            <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">Despliegues</h3>
                {canCommand && open && (
                    <form onSubmit={submitDeploy} className="mb-3 grid grid-cols-1 gap-2 rounded-xl border border-white/10 p-4 sm:grid-cols-6">
                        <select aria-label="Tipo de recurso" value={depForm.kind} onChange={(e) => setDepForm({ ...depForm, kind: e.target.value as ResourceKind, userId: '' })} className={inputCls}>
                            {(Object.keys(RESOURCE_KIND_LABEL) as ResourceKind[]).map((k) => <option key={k} value={k}>{RESOURCE_KIND_LABEL[k]}</option>)}
                        </select>
                        {depForm.kind === 'PERSONNEL' ? (
                            <select aria-label="Persona" value={depForm.userId} onChange={(e) => setDepForm({ ...depForm, userId: e.target.value })} className={`${inputCls} sm:col-span-2`}>
                                <option value="">Persona de la organización…</option>
                                {users.map((u) => <option key={u.id} value={u.id}>{u.fullName} ({u.username})</option>)}
                            </select>
                        ) : (
                            <input aria-label="Identificador" placeholder="Identificador (patente, nombre del equipo)" required value={depForm.label} onChange={(e) => setDepForm({ ...depForm, label: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                        )}
                        <input aria-label="Misión" placeholder="Misión" value={depForm.mission} onChange={(e) => setDepForm({ ...depForm, mission: e.target.value })} className={inputCls} />
                        <input aria-label="Sector" placeholder="Sector" value={depForm.sector} onChange={(e) => setDepForm({ ...depForm, sector: e.target.value })} className={inputCls} />
                        <button type="submit" className={btnPrimary}>Desplegar</button>
                        <input aria-label="Relevo previsto" type="datetime-local" value={depForm.plannedUntil} onChange={(e) => setDepForm({ ...depForm, plannedUntil: e.target.value })} className={`${inputCls} sm:col-span-2`} title="Relevo previsto" />
                        <input aria-label="Organización" placeholder="Organización (si es externo)" value={depForm.organization} onChange={(e) => setDepForm({ ...depForm, organization: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                        <p className="text-xs text-gray-500 sm:col-span-2">Una persona no puede estar en dos despliegues activos a la vez.</p>
                    </form>
                )}
                <ul className="space-y-2">
                    {deployments.length === 0 && <li className="text-xs text-gray-500">Sin despliegues.</li>}
                    {deployments.map((d) => (
                        <li key={d.id} className={`flex flex-wrap items-center gap-3 rounded-xl border border-white/10 p-3 text-sm ${d.active ? '' : 'opacity-50'}`}>
                            <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] uppercase text-gray-300">{RESOURCE_KIND_LABEL[d.kind]}</span>
                            <span className="font-medium text-white">{d.label}</span>
                            <span className="text-xs text-gray-500">{[d.organization, d.mission, d.sector].filter(Boolean).join(' · ')}</span>
                            <span className="ml-auto text-xs text-gray-500">
                                desde {fmt(d.startedAt)}{d.plannedUntil ? ` · relevo ${fmt(d.plannedUntil)}` : ''}{d.releasedAt ? ` · liberado ${fmt(d.releasedAt)}` : ''}
                            </span>
                            {canCommand && open && d.active && <>
                                <button onClick={() => extend(d)} className={btnGhost}>Prorrogar</button>
                                <button onClick={() => act(() => resourceService.release(emergencyId, d.id), 'Recurso liberado')} className={btnGhost}>Liberar</button>
                            </>}
                        </li>
                    ))}
                </ul>
            </section>

            <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">Solicitudes de recursos</h3>
                {canCommand && open && (
                    <form onSubmit={submitRequest} className="mb-3 grid grid-cols-1 gap-2 rounded-xl border border-white/10 p-4 sm:grid-cols-6">
                        <select aria-label="Tipo solicitado" value={reqForm.kind} onChange={(e) => setReqForm({ ...reqForm, kind: e.target.value as ResourceKind })} className={inputCls}>
                            {(Object.keys(RESOURCE_KIND_LABEL) as ResourceKind[]).map((k) => <option key={k} value={k}>{RESOURCE_KIND_LABEL[k]}</option>)}
                        </select>
                        <input aria-label="Cantidad solicitada" type="number" min={1} required value={reqForm.quantity} onChange={(e) => setReqForm({ ...reqForm, quantity: e.target.value })} className={inputCls} />
                        <input aria-label="Qué se necesita" placeholder="Qué se necesita" required value={reqForm.description} onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                        <input aria-label="A quién se pide" placeholder="A quién se pide" value={reqForm.target} onChange={(e) => setReqForm({ ...reqForm, target: e.target.value })} className={inputCls} />
                        <button type="submit" className={btnPrimary}>Solicitar</button>
                    </form>
                )}
                <ul className="space-y-2">
                    {requests.length === 0 && <li className="text-xs text-gray-500">Sin solicitudes.</li>}
                    {requests.map((r) => (
                        <li key={r.id} className="rounded-xl border border-white/10 p-3 text-sm">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${STATUS_COLOR[r.status]}`}>{REQUEST_STATUS_LABEL[r.status]}</span>
                                <span className="font-medium text-white">{r.quantity} × {RESOURCE_KIND_LABEL[r.kind].toLowerCase()}: {r.description}</span>
                                {r.target && <span className="text-xs text-gray-500">a {r.target}</span>}
                                {r.status !== 'PENDING' && <span className="text-xs text-gray-500">asignados {r.assignedQty}/{r.quantity}</span>}
                                <span className="ml-auto text-xs text-gray-500">{r.requestedName ?? '—'} · {fmt(r.createdAt)}</span>
                                {canCommand && open && (r.status === 'PENDING' || r.status === 'PARTIAL') && (
                                    <button onClick={() => setResponding({ id: r.id, status: 'ASSIGNED', assignedQty: '', note: '' })} className={btnGhost}>Responder</button>
                                )}
                            </div>
                            {r.responseNote && <p className="mt-1 text-xs text-gray-400">{r.respondedName ?? ''}: {r.responseNote}</p>}
                            {responding?.id === r.id && (
                                <form onSubmit={submitResponse} className="mt-3 flex flex-wrap items-center gap-2">
                                    <select aria-label="Respuesta" value={responding.status} onChange={(e) => setResponding({ ...responding, status: e.target.value as RequestStatus })} className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white">
                                        <option value="ASSIGNED">Asignar completa</option>
                                        <option value="PARTIAL">Asignar parcial</option>
                                        <option value="REJECTED">Rechazar</option>
                                        <option value="CANCELLED">Cancelar</option>
                                    </select>
                                    {responding.status === 'PARTIAL' && (
                                        <input aria-label="Cantidad asignada" type="number" min={1} max={r.quantity - 1} required value={responding.assignedQty}
                                            onChange={(e) => setResponding({ ...responding, assignedQty: e.target.value })} className="w-24 rounded-xl border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white" />
                                    )}
                                    <input aria-label="Nota de respuesta" placeholder="Nota" value={responding.note} onChange={(e) => setResponding({ ...responding, note: e.target.value })} className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white" />
                                    <button type="submit" className={btnPrimary}>Confirmar</button>
                                    <button type="button" onClick={() => setResponding(null)} className={btnGhost}>Cancelar</button>
                                </form>
                            )}
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    )
}

function Tile({ value, label, valueClass = 'text-white' }: { value: number | string; label: string; valueClass?: string }) {
    return (
        <div className="rounded-xl border border-white/10 p-3">
            <div className={`text-2xl font-bold ${valueClass}`}>{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
        </div>
    )
}
